"use client";

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useTranslation } from "@/lib/i18n/context";
import { TUTORIALS_COPY, type TutorialLang, type TutorialGuide } from "@/lib/tutorials/data";

/** Inline-markdown renderer: `**bold**`, `*italic*`, `` `code` ``, `[label](href)`. */
function renderText(s: string) {
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\)|\*[^*]+\*)/g;
  const parts = s.split(re);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return (
        <strong key={i} className="font-medium text-[var(--ink)]">
          {p.slice(2, -2)}
        </strong>
      );
    }
    if (p.startsWith("`") && p.endsWith("`")) {
      return (
        <code
          key={i}
          className="rounded bg-[var(--paper-2)] px-1.5 py-0.5 font-mono text-[0.85em] text-[var(--ink)]"
        >
          {p.slice(1, -1)}
        </code>
      );
    }
    const link = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      return (
        <Link
          key={i}
          href={link[2]}
          className="text-[var(--terracotta)] underline decoration-[var(--terracotta)]/30 underline-offset-2 hover:decoration-[var(--terracotta)]"
        >
          {link[1]}
        </Link>
      );
    }
    if (p.startsWith("*") && p.endsWith("*") && p.length > 2) {
      return (
        <em key={i} className="font-display italic text-[var(--ink)]">
          {p.slice(1, -1)}
        </em>
      );
    }
    return <Fragment key={i}>{p}</Fragment>;
  });
}

/** Strip inline markup + fold case/diacritics so search matches "soboto" etc. */
function fold(s: string): string {
  return s
    .replace(/[*`]/g, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

function plainText(s: string): string {
  return s.replace(/[*`]/g, "").replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
}

function Callout({ kind, text }: { kind: "tip" | "note"; text: string }) {
  const isSl = useTranslation().lang !== "en";
  const label = kind === "tip" ? "Tip" : isSl ? "Opomba" : "Note";
  return (
    <div className="mt-3 flex gap-3 border-l-2 border-[var(--terracotta)]/40 bg-[var(--paper-2)]/40 py-2.5 pl-3.5 pr-4">
      <span className="mt-0.5 shrink-0 font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--terracotta)]">
        {label}
      </span>
      <p className="m-0 font-display text-[14.5px] leading-[1.6] text-[var(--ink-soft)]">
        {renderText(text)}
      </p>
    </div>
  );
}

function GuideBlock({ guide }: { guide: TutorialGuide }) {
  return (
    <section id={guide.id} className="scroll-mt-24">
      <div className="mb-4 flex items-baseline gap-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-soft)]">
          {guide.eyebrow}
        </span>
        <span className="h-px flex-1 bg-[var(--rule)]" />
      </div>
      <h2 className="font-display text-[clamp(24px,3vw,32px)] leading-tight tracking-[-0.02em] text-[var(--ink)]">
        {guide.title}
      </h2>
      {guide.intro && (
        <p className="mt-2.5 max-w-[640px] font-display text-[16px] leading-[1.6] text-[var(--ink-soft)]">
          {renderText(guide.intro)}
        </p>
      )}
      <ol className="mt-6 space-y-7">
        {guide.steps.map((step, i) => (
          <li key={i} id={`${guide.id}-s${i}`} className="flex scroll-mt-24 gap-4">
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[var(--rule)] font-mono text-[12px] text-[var(--ink-soft)]">
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="font-display text-[18px] leading-snug text-[var(--ink)]">
                {step.title}
              </h3>
              <p className="mt-1 max-w-[640px] font-display text-[15.5px] leading-[1.65] text-[var(--ink-soft)]">
                {renderText(step.body)}
              </p>
              {step.callout && <Callout kind={step.callout.kind} text={step.callout.text} />}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

interface SearchEntry {
  guideId: string;
  guideTitle: string;
  eyebrow: string;
  anchor: string;
  title: string;
  snippet: string;
  haystack: string;
}

export function TutorialsContent() {
  const { lang } = useTranslation();
  const langKey: TutorialLang = lang === "en" ? "en" : "sl";
  const isSl = langKey === "sl";
  const cp = TUTORIALS_COPY[langKey];

  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState<string>(cp.quickstart.id);
  const searching = query.trim().length > 0;

  const allGuides = useMemo(() => [cp.quickstart, ...cp.guides], [cp]);

  // Flat search index: one entry per step (+ one per guide intro/title).
  const index = useMemo<SearchEntry[]>(() => {
    const out: SearchEntry[] = [];
    for (const g of allGuides) {
      out.push({
        guideId: g.id,
        guideTitle: g.title,
        eyebrow: g.eyebrow,
        anchor: g.id,
        title: g.title,
        snippet: g.intro ?? g.steps[0]?.body ?? "",
        haystack: fold([g.title, g.eyebrow, g.intro ?? ""].join(" ")),
      });
      g.steps.forEach((s, i) => {
        out.push({
          guideId: g.id,
          guideTitle: g.title,
          eyebrow: g.eyebrow,
          anchor: `${g.id}-s${i}`,
          title: s.title,
          snippet: s.body,
          haystack: fold([s.title, s.body, s.callout?.text ?? "", g.title].join(" ")),
        });
      });
    }
    return out;
  }, [allGuides]);

  const results = useMemo<SearchEntry[]>(() => {
    if (!searching) return [];
    const terms = fold(query).split(/\s+/).filter(Boolean);
    return index.filter((e) => terms.every((t) => e.haystack.includes(t)));
  }, [index, query, searching]);

  // Scrollspy — highlight the section currently in view (only when not searching).
  useEffect(() => {
    if (searching) return;
    const sections = allGuides
      .map((g) => document.getElementById(g.id))
      .filter((el): el is HTMLElement => el !== null);
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target.id) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-80px 0px -65% 0px", threshold: 0 }
    );
    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [allGuides, searching]);

  const t = {
    contents: isSl ? "Kazalo" : "Contents",
    searchPlaceholder: isSl ? "Išči po navodilih…" : "Search the tutorials…",
    resultsLabel: (n: number) =>
      isSl
        ? `${n} ${n === 1 ? "zadetek" : n === 2 ? "zadetka" : n < 5 ? "zadetki" : "zadetkov"}`
        : `${n} ${n === 1 ? "result" : "results"}`,
    noResults: isSl ? "Ni zadetkov. Poskusi z drugo besedo." : "No results. Try another word.",
    clear: isSl ? "Počisti" : "Clear",
  };

  return (
    <main className="mx-auto max-w-[1280px] px-6 pb-32 pt-12 text-[var(--ink)]">
      {/* Header — mirrors the in-app /help + /faq template. */}
      <header className="mb-10 border-b border-[var(--rule)]/30 pb-6">
        <p className="mb-2.5 flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-soft)]">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--terracotta)]" />
          {cp.eyebrow}
        </p>
        <h1 className="font-display text-[clamp(40px,5vw,64px)] leading-[0.95] tracking-[-0.025em] text-balance">
          {cp.title.a}
          <em className="font-medium italic text-[var(--terracotta)]">{cp.title.em}</em>.
        </h1>
        <p className="mt-3 max-w-[720px] font-display text-[17px] italic leading-[1.5] text-[var(--ink-soft)]">
          {cp.subtitle}
        </p>
      </header>

      <div className="lg:grid lg:grid-cols-[230px_minmax(0,1fr)] lg:gap-12">
        {/* Left rail — sticky search + table of contents (lg+). */}
        <aside className="hidden lg:block">
          <div className="sticky top-[88px] max-h-[calc(100vh-110px)] overflow-y-auto pr-2">
            <SearchInput
              value={query}
              onChange={setQuery}
              placeholder={t.searchPlaceholder}
              clearLabel={t.clear}
            />
            <nav aria-label={t.contents} className="mt-6">
              <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-soft)]">
                {t.contents}
              </p>
              <ul className="space-y-1 border-l border-[var(--rule)]">
                {allGuides.map((g) => {
                  const active = !searching && activeId === g.id;
                  return (
                    <li key={g.id}>
                      <a
                        href={`#${g.id}`}
                        className={`-ml-px block border-l-2 py-1.5 pl-3.5 font-display text-[14.5px] leading-snug transition ${
                          active
                            ? "border-[var(--terracotta)] text-[var(--terracotta)]"
                            : "border-transparent text-[var(--ink-soft)] hover:border-[var(--rule)] hover:text-[var(--ink)]"
                        }`}
                      >
                        {g.title}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>
        </aside>

        {/* Main column. */}
        <div className="min-w-0 max-w-[820px]">
          {/* Mobile search (hidden on lg, where the rail shows it). */}
          <div className="mb-8 lg:hidden">
            <SearchInput
              value={query}
              onChange={setQuery}
              placeholder={t.searchPlaceholder}
              clearLabel={t.clear}
            />
          </div>

          {searching ? (
            <div>
              <p className="mb-6 font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--ink-soft)]">
                {results.length > 0 ? t.resultsLabel(results.length) : t.noResults}
              </p>
              <ul className="space-y-5">
                {results.map((r) => (
                  <li key={r.anchor} className="border-b border-[var(--hair)] pb-5 last:border-b-0">
                    <a href={`#${r.anchor}`} className="group block">
                      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--ink-soft)]">
                        {r.eyebrow}
                      </span>
                      <span className="mt-1.5 block font-display text-[18px] leading-snug text-[var(--ink)] group-hover:text-[var(--terracotta)]">
                        {r.title}
                      </span>
                      <span className="mt-1 block max-w-[640px] font-display text-[15px] leading-[1.6] text-[var(--ink-soft)]">
                        {plainText(r.snippet)}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="space-y-16">
              <GuideBlock guide={cp.quickstart} />

              {/* Next — jump links to the topic guides. */}
              <nav aria-label={cp.nextLabel}>
                <div className="mb-4 flex items-baseline gap-3">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-soft)]">
                    {cp.nextLabel}
                  </span>
                  <span className="h-px flex-1 bg-[var(--rule)]" />
                </div>
                <ul className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
                  {cp.guides.map((g) => (
                    <li key={g.id}>
                      <a
                        href={`#${g.id}`}
                        className="font-display text-[17px] italic text-[var(--ink)] underline-offset-4 hover:text-[var(--terracotta)] hover:underline"
                      >
                        {g.title} →
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              {cp.guides.map((g) => (
                <GuideBlock key={g.id} guide={g} />
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

function SearchInput({
  value,
  onChange,
  placeholder,
  clearLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  clearLabel: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div className="relative">
      <input
        ref={ref}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border border-[var(--rule)] bg-[var(--paper-2)]/40 py-2 pl-3 pr-8 font-display text-[14.5px] text-[var(--ink)] placeholder:text-[var(--ink-soft)] focus:border-[var(--terracotta)] focus:outline-none focus:ring-1 focus:ring-[var(--terracotta)]/30"
      />
      {value && (
        <button
          type="button"
          aria-label={clearLabel}
          onClick={() => {
            onChange("");
            ref.current?.focus();
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 font-mono text-[13px] text-[var(--ink-soft)] hover:text-[var(--terracotta)]"
        >
          ×
        </button>
      )}
    </div>
  );
}
